import { RefObject, useCallback, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.min.css";
import * as api from "../../utils/Api.ts";
import { showMsg } from "../../utils/Constants.tsx";
import ChartCard from "../../components/widgets/graphs/ChartCard.tsx";
import KpiCard from "../../components/widgets/graphs/KpiCard.tsx";
import MultiBarChart from "../../components/widgets/graphs/MultiBarChart.tsx";
import ComboBarLineChart from "../../components/widgets/graphs/ComboBarLineChart.tsx";
import DoughnutChart from "../../components/widgets/graphs/DoughnutChart.tsx";
import {
  CHART_COLORS,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatPercent,
} from "../../components/widgets/graphs/chartTheme.ts";
import ReportesHeader from "../../components/reportes/ReportesHeader.tsx";
import EstadosBoxes, {
  EstadoBox,
  MantencionLink,
} from "../../components/reportes/EstadosBoxes.tsx";
import {
  CICLO_SOLICITUD,
  codigosCiclo,
} from "../../components/reportes/CicloVidaCard.tsx";
import TrazabilidadCard from "../../components/reportes/TrazabilidadCard.tsx";
import SubroganciaSection, {
  Subrogancia,
  SubroganciaResumen,
  subroganciaSheets,
} from "../../components/reportes/SubroganciaSection.tsx";
import { veSubroganciaCompleta } from "../../components/reportes/accesoReportes.ts";
import {
  Count,
  FilterLink,
  Money,
  PctBar,
} from "../../components/reportes/ReportesCells.tsx";
import {
  AGRUPACION_OPTIONS,
  ORDENAR_POR_OPTIONS,
  TiemposCiclo,
  clean,
  estadoAprobacionColor,
  exportSheets,
  filtrosSheet,
  formatDias,
  formatFecha,
  formatPeriodo,
  useCatalogos,
  useFiltrosReporte,
  useReporte,
  useScrollbarGutter,
} from "../../components/reportes/reportesUtils.ts";

/* ------------------------------------------------------------------ */
/* Types (shape returned by /reports/*)                                 */
/* ------------------------------------------------------------------ */

interface ResumenSolicitudes {
  cantidadSolicitudes: number;
  cantidadLineas: number;
  cantidadUsuarios: number;
  cantidadCentrosCosto: number;
  lineasPendientes: number;
  lineasAprobadas: number;
  lineasRechazadas: number;
  lineasAnuladas: number;
  lineasEmitidas: number;
  montoSolicitadoClp: number;
  montoAprobadoClp: number;
  montoRechazadoClp: number;
  tasaAprobacionMonto: number;
  tasaConversionAOrden: number;
}

interface Resumen {
  solicitudes: ResumenSolicitudes;
}

interface EstadoRow {
  idEstado: number;
  codigo: string;
  nombre: string;
  cantidad: number;
  montoClp: number;
  porcentaje: number;
}

interface Estados {
  porSolicitud: EstadoRow[];
  porLinea: EstadoRow[];
}

interface PeriodoRow {
  periodo: string;
  cantidadSolicitudes: number;
  cantidadLineas: number;
  cantidadUsuarios: number;
  montoSolicitadoClp: number;
  montoAprobadoClp: number;
  pendientes: number;
  anuladas: number;
  rechazadas: number;
  aprobadas: number;
  confirmadas: number;
  emitidas: number;
}

interface TipoRow {
  idTipoDocumento: number;
  nombre: string;
  cantidadSolicitudes: number;
  montoClp: number;
  aprobadas: number;
  rechazadas: number;
  porcentajeMonto: number;
}

interface CentroCostoRow {
  idCentroCosto: number;
  codigo: string;
  nombre: string;
  area: string;
  cantidadSolicitudes: number;
  cantidadLineas: number;
  cantidadUsuarios: number;
  cantidadProveedores: number;
  cantidadItems: number;
  montoSolicitadoClp: number;
  montoAprobadoClp: number;
  montoRechazadoClp: number;
  lineasPendientes: number;
  lineasAprobadas: number;
  lineasRechazadas: number;
  lineasEmitidas: number;
  tasaAprobacion: number;
  tasaConversionAOrden: number;
}

interface UsuarioRow {
  idUsuario: number;
  usuario: string;
  email: string;
  cantidadSolicitudes: number;
  cantidadLineas: number;
  cantidadCentrosCosto: number;
  montoSolicitadoClp: number;
  montoAprobadoClp: number;
  pendientes: number;
  aprobadas: number;
  rechazadas: number;
  anuladas: number;
  emitidas: number;
  tasaAprobacion: number;
  tasaRechazo: number;
  promedioDiasAOrden: number | null;
  primeraSolicitud: string;
  ultimaSolicitud: string;
}

interface AprobadorRow {
  idUsuario: number;
  usuario: string;
  acciones: number;
  solicitudes: number;
  aprobaciones: number;
  confirmaciones: number;
  rechazos: number;
  anulaciones: number;
  montoAprobadoClp: number;
  tasaRechazo: number;
  promedioDiasDesdeCreacion: number | null;
  ultimaAccion: string;
}

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const RANKING_LIMIT = 50;
const RANKING_CHART = 10;
const APROBADORES_LIMIT = 20;

// Series of the "estados por período" stacked chart
const PERIODO_ESTADOS: {
  key: keyof PeriodoRow;
  label: string;
  codigo: string;
}[] = [
  { key: "emitidas", label: "Emitidas", codigo: "EMIT" },
  { key: "confirmadas", label: "Confirmadas", codigo: "CONF" },
  { key: "aprobadas", label: "Aprobadas", codigo: "APRO" },
  { key: "pendientes", label: "Pendientes", codigo: "PEND" },
  { key: "rechazadas", label: "Rechazadas", codigo: "RECH" },
  { key: "anuladas", label: "Anuladas", codigo: "ANUL" },
];

// Life-cycle stages exported to Excel ("Tiempos ciclo" sheet)
const TRAMOS_SOLICITUD = codigosCiclo(CICLO_SOLICITUD);

const ESTADOS_VISTA_OPTIONS = [
  { label: "Solicitudes", value: "porSolicitud" },
  { label: "Líneas", value: "porLinea" },
];

// Big-number boxes (partial states excluded, like the legacy index)
const ESTADOS_BOXES_SOLICITUD: EstadoBox[] = [
  { codigo: "PEND", titulo: "Pendientes", estado: "Pendiente" },
  { codigo: "ANUL", titulo: "Anuladas", estado: "Anulada" },
  { codigo: "RECH", titulo: "Rechazadas", estado: "Rechazada" },
  { codigo: "APRO", titulo: "Aprobadas", estado: "Aprobada" },
  { codigo: "CONF", titulo: "Confirmadas", estado: "Confirmada" },
  { codigo: "EMIT", titulo: "Emitidas", estado: "Emitida" },
];

// Same destination as the legacy NumberBox links
const MANTENCION_LINK: MantencionLink = {
  ruta: "solicitudes-compra/mantencion",
  paramEstado: "socest_nombre",
  paramFecha: "soc_dt_cre",
};

/* ------------------------------------------------------------------ */
/* Screen                                                               */
/* ------------------------------------------------------------------ */

export default function DashboardSolicitudesCompra() {
  const toast = useRef<Toast>(null);
  const usuariosRef = useRef<HTMLDivElement>(null);
  const trazabilidadRef = useRef<HTMLDivElement>(null);

  // Users without cost center 29 also see the solicitudes they created
  // in other cost centers (backend alcanceSolicitante)
  const filtros = useFiltrosReporte({ incluirSolicitudesPropias: true });
  const { query, queryKey } = filtros;
  const catalogos = useCatalogos();
  useScrollbarGutter();

  const [agrupacion, setAgrupacion] = useState("mes");
  const [estadosVista, setEstadosVista] =
    useState<keyof Estados>("porSolicitud");
  const [ordenCC, setOrdenCC] = useState("monto");
  const [ordenUsuarios, setOrdenUsuarios] = useState("monto");
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const R = api.GET_REPORTES;
  // Presupuesto inside resumen is by calendar year: use the "hasta" year
  const anio = Number(
    (query.fechaHasta ?? "").substring(0, 4) || new Date().getFullYear(),
  );

  const resumen = useReporte<Resumen>(
    () => R.getResumen({ ...query, anio }),
    queryKey,
  );
  const periodos = useReporte<PeriodoRow[]>(
    () => R.getSolicitudesPeriodos({ ...query, agrupacion }),
    `${queryKey}|${agrupacion}`,
  );
  const estados = useReporte<Estados>(
    () => R.getSolicitudesEstados(query),
    queryKey,
  );
  const tipos = useReporte<TipoRow[]>(
    () => R.getSolicitudesTipos(query),
    queryKey,
  );
  const centros = useReporte<CentroCostoRow[]>(
    () =>
      R.getSolicitudesCentrosCosto({
        ...query,
        limit: RANKING_LIMIT,
        ordenarPor: ordenCC,
      }),
    `${queryKey}|${ordenCC}`,
  );
  const usuarios = useReporte<UsuarioRow[]>(
    () =>
      R.getSolicitudesUsuarios({
        ...query,
        limit: RANKING_LIMIT,
        ordenarPor: ordenUsuarios,
      }),
    `${queryKey}|${ordenUsuarios}`,
  );
  const aprobadores = useReporte<AprobadorRow[]>(
    () => R.getSolicitudesAprobadores({ ...query, limit: APROBADORES_LIMIT }),
    queryKey,
  );
  const tiempos = useReporte<TiemposCiclo>(
    () => R.getCicloTiempos(query),
    queryKey,
  );
  // Subrogancia: every pair only for the full-view users (81, 65); the
  // user's own pairs for everyone (empty when not in usuarios_subrogantes)
  const verTodaSubrogancia = veSubroganciaCompleta(filtros.idUsuarioActual);
  const subrogancia = useReporte<Subrogancia | null>(
    () =>
      verTodaSubrogancia
        ? R.getSolicitudesSubrogantes(query)
        : Promise.resolve(null),
    `${queryKey}|${verTodaSubrogancia}`,
  );
  const subroganciaPropia = useReporte<SubroganciaResumen | null>(
    () =>
      filtros.idUsuarioActual
        ? R.getSubrogantesResumen({
            ...query,
            idUser: filtros.idUsuarioActual,
          })
        : Promise.resolve(null),
    `${queryKey}|${filtros.idUsuarioActual}`,
  );

  const reportes = [
    resumen,
    periodos,
    estados,
    tipos,
    centros,
    usuarios,
    aprobadores,
    tiempos,
    subrogancia,
    subroganciaPropia,
  ];
  const anyLoading = reportes.some((rep) => rep.loading);

  const reloadAll = () => {
    reportes.forEach((rep) => rep.reload());
    setRefreshKey((k) => k + 1);
  };

  // Solicitudes use tipo documento ids 1-4 (the catalog repeats the same
  // names with ids 5-8 for órdenes)
  const tiposDocumentoOptions = useMemo(
    () =>
      (catalogos.data?.tiposDocumento ?? [])
        .filter((t) => t.id <= 4)
        .map((t) => ({ label: clean(t.nombre), value: t.id }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [catalogos.data],
  );

  const scrollTo = (ref: RefObject<HTMLDivElement>) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ------------------------- derived chart data ------------------------- */

  const periodoChart = useMemo(() => {
    const rows = periodos.data ?? [];
    return {
      labels: rows.map((r) => formatPeriodo(r.periodo)),
      bars: [
        {
          label: "Solicitado",
          data: rows.map((r) => r.montoSolicitadoClp),
          color: CHART_COLORS.ordenado,
        },
        {
          label: "Aprobado",
          data: rows.map((r) => r.montoAprobadoClp),
          color: CHART_COLORS.facturado,
        },
      ],
      lines: [
        {
          label: "N° solicitudes",
          data: rows.map((r) => r.cantidadSolicitudes),
          color: CHART_COLORS.pendiente,
        },
      ],
      estados: PERIODO_ESTADOS.map((e) => ({
        label: e.label,
        data: rows.map((r) => Number(r[e.key]) || 0),
        color: estadoAprobacionColor(e.codigo),
      })),
    };
  }, [periodos.data]);

  const estadosChart = useMemo(() => {
    const rows = (estados.data?.[estadosVista] ?? []).filter(
      (r) => r.cantidad > 0,
    );
    return {
      rows,
      labels: rows.map((r) => r.nombre),
      values: rows.map((r) => r.cantidad),
      colors: rows.map((r) => estadoAprobacionColor(r.codigo)),
      total: rows.reduce((a, r) => a + r.cantidad, 0),
    };
  }, [estados.data, estadosVista]);

  const tiposChart = useMemo(() => {
    const rows = tipos.data ?? [];
    return {
      labels: rows.map((r) => clean(r.nombre)),
      values: rows.map((r) => r.montoClp),
      total: rows.reduce((a, r) => a + r.montoClp, 0),
    };
  }, [tipos.data]);

  const topCentros = useMemo(
    () => (centros.data ?? []).slice(0, RANKING_CHART),
    [centros.data],
  );

  const centrosChart = useMemo(
    () => ({
      labels: topCentros.map((c) => clean(c.nombre)),
      series:
        ordenCC === "cantidad"
          ? [
              {
                label: "Solicitudes",
                data: topCentros.map((c) => c.cantidadSolicitudes),
                color: CHART_COLORS.facturado,
              },
              {
                label: "Líneas",
                data: topCentros.map((c) => c.cantidadLineas),
                color: CHART_COLORS.neutral,
              },
            ]
          : [
              {
                label: "Solicitado",
                data: topCentros.map((c) => c.montoSolicitadoClp),
                color: CHART_COLORS.ordenado,
              },
              {
                label: "Aprobado",
                data: topCentros.map((c) => c.montoAprobadoClp),
                color: CHART_COLORS.facturado,
              },
              {
                label: "Rechazado",
                data: topCentros.map((c) => c.montoRechazadoClp),
                color: CHART_COLORS.diferencia,
              },
            ],
    }),
    [topCentros, ordenCC],
  );

  // Stable handler: an inline one would rebuild the chart options (and replay
  // the animation) on every re-render, e.g. when the agrupación changes
  const { filterByCentroCosto } = filtros;
  const onCentroBarClick = useCallback(
    (index: number) => filterByCentroCosto(topCentros[index]?.idCentroCosto),
    [topCentros, filterByCentroCosto],
  );

  /* ------------------------- excel ------------------------- */

  const exportExcel = async () => {
    setExporting(true);
    try {
      const s = resumen.data?.solicitudes;
      await exportSheets("Reporte_Solicitudes", [
        filtrosSheet(
          filtros.applied,
          catalogos.data,
          filtros.alcance,
          filtros.incluyePropias,
        ),
        {
          name: "Resumen",
          rows: s
            ? [
                { Indicador: "Solicitudes", Valor: s.cantidadSolicitudes },
                { Indicador: "Líneas", Valor: s.cantidadLineas },
                { Indicador: "Usuarios", Valor: s.cantidadUsuarios },
                {
                  Indicador: "Centros de costo",
                  Valor: s.cantidadCentrosCosto,
                },
                { Indicador: "Líneas pendientes", Valor: s.lineasPendientes },
                { Indicador: "Líneas aprobadas", Valor: s.lineasAprobadas },
                { Indicador: "Líneas rechazadas", Valor: s.lineasRechazadas },
                { Indicador: "Líneas anuladas", Valor: s.lineasAnuladas },
                { Indicador: "Líneas emitidas", Valor: s.lineasEmitidas },
                {
                  Indicador: "Monto solicitado CLP",
                  Valor: s.montoSolicitadoClp,
                },
                { Indicador: "Monto aprobado CLP", Valor: s.montoAprobadoClp },
                {
                  Indicador: "Monto rechazado CLP",
                  Valor: s.montoRechazadoClp,
                },
                {
                  Indicador: "% aprobación (monto)",
                  Valor: s.tasaAprobacionMonto,
                },
                {
                  Indicador: "% conversión a OC",
                  Valor: s.tasaConversionAOrden,
                },
              ]
            : [],
        },
        {
          name: "Periodos",
          rows: (periodos.data ?? []).map((p) => ({
            Periodo: p.periodo,
            Solicitudes: p.cantidadSolicitudes,
            Líneas: p.cantidadLineas,
            Usuarios: p.cantidadUsuarios,
            "Solicitado CLP": p.montoSolicitadoClp,
            "Aprobado CLP": p.montoAprobadoClp,
            Pendientes: p.pendientes,
            Aprobadas: p.aprobadas,
            Confirmadas: p.confirmadas,
            Emitidas: p.emitidas,
            Rechazadas: p.rechazadas,
            Anuladas: p.anuladas,
          })),
        },
        ...(["porSolicitud", "porLinea"] as const).map((vista) => ({
          name:
            vista === "porSolicitud" ? "Estados solicitud" : "Estados linea",
          rows: (estados.data?.[vista] ?? []).map((e) => ({
            Estado: e.nombre,
            Cantidad: e.cantidad,
            "Monto CLP": e.montoClp,
            "%": e.porcentaje,
          })),
        })),
        {
          name: "Tipos",
          rows: (tipos.data ?? []).map((t) => ({
            Tipo: clean(t.nombre),
            Solicitudes: t.cantidadSolicitudes,
            "Monto CLP": t.montoClp,
            Aprobadas: t.aprobadas,
            Rechazadas: t.rechazadas,
            "% monto": t.porcentajeMonto,
          })),
        },
        {
          name: "Centros costo",
          rows: (centros.data ?? []).map((c) => ({
            Código: clean(c.codigo),
            "Centro de costo": clean(c.nombre),
            Área: clean(c.area),
            Solicitudes: c.cantidadSolicitudes,
            Líneas: c.cantidadLineas,
            Usuarios: c.cantidadUsuarios,
            Proveedores: c.cantidadProveedores,
            Ítems: c.cantidadItems,
            "Solicitado CLP": c.montoSolicitadoClp,
            "Aprobado CLP": c.montoAprobadoClp,
            "Rechazado CLP": c.montoRechazadoClp,
            "Líneas pendientes": c.lineasPendientes,
            "% aprobación": c.tasaAprobacion,
            "% conversión a OC": c.tasaConversionAOrden,
          })),
        },
        {
          name: "Usuarios",
          rows: (usuarios.data ?? []).map((u) => ({
            Usuario: u.usuario,
            Email: u.email,
            Solicitudes: u.cantidadSolicitudes,
            Líneas: u.cantidadLineas,
            "Centros de costo": u.cantidadCentrosCosto,
            "Solicitado CLP": u.montoSolicitadoClp,
            "Aprobado CLP": u.montoAprobadoClp,
            Pendientes: u.pendientes,
            Rechazadas: u.rechazadas,
            Anuladas: u.anuladas,
            Emitidas: u.emitidas,
            "% aprobación": u.tasaAprobacion,
            "% rechazo": u.tasaRechazo,
            "Días promedio a OC": u.promedioDiasAOrden,
            "Última solicitud": formatFecha(u.ultimaSolicitud),
          })),
        },
        {
          name: "Aprobadores",
          rows: (aprobadores.data ?? []).map((a) => ({
            Usuario: a.usuario,
            Acciones: a.acciones,
            Solicitudes: a.solicitudes,
            Aprobaciones: a.aprobaciones,
            Confirmaciones: a.confirmaciones,
            Rechazos: a.rechazos,
            Anulaciones: a.anulaciones,
            "Monto aprobado CLP": a.montoAprobadoClp,
            "% rechazo": a.tasaRechazo,
            "Días promedio desde creación": a.promedioDiasDesdeCreacion,
            "Última acción": formatFecha(a.ultimaAccion),
          })),
        },
        // Same visibility as the screen (see SubroganciaSection)
        ...subroganciaSheets(
          "solicitudes",
          verTodaSubrogancia,
          subrogancia.data,
          subroganciaPropia.data,
        ),
        {
          name: "Tiempos ciclo",
          rows: (tiempos.data?.tramos ?? [])
            .filter((t) => TRAMOS_SOLICITUD.includes(t.codigo))
            .map((t) => ({
              Tramo: t.nombre,
              Casos: t.cantidad,
              "Promedio (días)": t.promedio,
              "Mediana (días)": t.mediana,
              "P90 (días)": t.p90,
              "Mínimo (días)": t.minimo,
              "Máximo (días)": t.maximo,
              Descartados: t.descartados,
            })),
        },
      ]);
    } catch (e) {
      console.error("Error exportando reporte solicitudes:", e);
      showMsg(toast, "error", "Error", "No fue posible generar el Excel");
    } finally {
      setExporting(false);
    }
  };

  /* ------------------------- render ------------------------- */

  const s = resumen.data?.solicitudes;
  const lineasCerradasNegativas =
    (s?.lineasRechazadas ?? 0) + (s?.lineasAnuladas ?? 0);

  return (
    <div className="fd-dashboard rp-dashboard rp-tema-solicitudes">
      <Toast ref={toast} />

      <ReportesHeader
        eyebrow="Compras · Solicitudes"
        title="Reporte de solicitudes de compra"
        icon="pi-file-edit"
        filtros={filtros}
        catalogos={catalogos.data}
        toast={toast}
        tiposDocumento={tiposDocumentoOptions}
        loading={anyLoading}
        exporting={exporting}
        onReload={reloadAll}
        onExport={exportExcel}
      />

      {/* ------------------------- ESTADOS (big numbers) ------------------------- */}
      <div className="rp-estados-pila">
        <EstadosBoxes
          titulo="Documentos SOC"
          nota="Cantidad de solicitudes por estado · clic para ver en mantención"
          estados={estados.data?.porSolicitud ?? null}
          boxes={ESTADOS_BOXES_SOLICITUD}
          link={MANTENCION_LINK}
          fechaDesde={query.fechaDesde}
          fechaHasta={query.fechaHasta}
          loading={estados.loading}
          error={estados.error}
        />
        <EstadosBoxes
          titulo="Líneas SOC"
          nota="Cantidad de líneas de detalle por estado de la línea"
          estados={estados.data?.porLinea ?? null}
          boxes={ESTADOS_BOXES_SOLICITUD}
          link={MANTENCION_LINK}
          loading={estados.loading}
          error={estados.error}
          navegable={false}
        />
      </div>

      {/* ------------------------- KPIs ------------------------- */}
      {resumen.error ? (
        <ChartCard
          title="Resumen"
          error={resumen.error}
          onRetry={resumen.reload}
          height={80}
        />
      ) : (
        <div className="fd-kpis">
          <KpiCard
            title="Solicitudes"
            icon="pi-file-edit"
            color={CHART_COLORS.ordenado}
            loading={resumen.loading}
            value={formatNumber(s?.cantidadSolicitudes)}
            hint={`${formatNumber(s?.cantidadLineas)} líneas · ${formatNumber(
              s?.cantidadCentrosCosto,
            )} centros de costo`}
          />
          <KpiCard
            title="Monto solicitado"
            icon="pi-shopping-cart"
            color={CHART_COLORS.ordenado}
            loading={resumen.loading}
            value={formatMoney(s?.montoSolicitadoClp)}
            hint="Total de líneas, en CLP"
          />
          <KpiCard
            title="Monto aprobado"
            icon="pi-check-circle"
            color={CHART_COLORS.facturado}
            loading={resumen.loading}
            value={formatMoney(s?.montoAprobadoClp)}
            progress={s?.tasaAprobacionMonto ?? 0}
            hint={`${formatPercent(s?.tasaAprobacionMonto)} del monto solicitado`}
          />
          <KpiCard
            title="Conversión a orden de compra"
            icon="pi-arrow-right-arrow-left"
            color={CHART_COLORS.contabilizado}
            loading={resumen.loading}
            value={formatPercent(s?.tasaConversionAOrden)}
            progress={s?.tasaConversionAOrden ?? 0}
            hint={`${formatNumber(s?.lineasEmitidas)} líneas emitidas en OC`}
          />
          <KpiCard
            title="Líneas pendientes"
            icon="pi-hourglass"
            color={CHART_COLORS.pendiente}
            tone={(s?.lineasPendientes ?? 0) > 0 ? "warning" : "success"}
            loading={resumen.loading}
            value={formatNumber(s?.lineasPendientes)}
            progress={
              s?.cantidadLineas
                ? (s.lineasPendientes / s.cantidadLineas) * 100
                : 0
            }
            hint="Ver trazabilidad"
            onClick={() => scrollTo(trazabilidadRef)}
          />
          <KpiCard
            title="Monto rechazado"
            icon="pi-times-circle"
            color={CHART_COLORS.diferencia}
            tone={(s?.montoRechazadoClp ?? 0) > 0 ? "danger" : "success"}
            loading={resumen.loading}
            value={formatMoney(s?.montoRechazadoClp)}
            progress={
              s?.montoSolicitadoClp
                ? (s.montoRechazadoClp / s.montoSolicitadoClp) * 100
                : 0
            }
            hint={`${formatNumber(s?.lineasRechazadas)} líneas rechazadas`}
          />
          <KpiCard
            title="Líneas rechazadas / anuladas"
            icon="pi-ban"
            color={CHART_COLORS.neutral}
            tone={lineasCerradasNegativas > 0 ? "warning" : "success"}
            loading={resumen.loading}
            value={formatNumber(lineasCerradasNegativas)}
            hint={`${formatNumber(s?.lineasRechazadas)} rechazadas · ${formatNumber(
              s?.lineasAnuladas,
            )} anuladas`}
          />
          <KpiCard
            title="Usuarios solicitantes"
            icon="pi-users"
            color={CHART_COLORS.neutral}
            loading={resumen.loading}
            value={formatNumber(s?.cantidadUsuarios)}
            hint="Ver ranking de usuarios"
            onClick={() => scrollTo(usuariosRef)}
          />
        </div>
      )}

      {/* ------------------------- PERIODO + ESTADOS ------------------------- */}
      <div className="fd-grid fd-grid-2-1">
        <ChartCard
          title="Evolución de solicitudes"
          subtitle="Monto solicitado vs aprobado (CLP) y N° de solicitudes"
          loading={periodos.loading}
          error={periodos.error}
          onRetry={periodos.reload}
          empty={!periodos.data?.length}
          actions={
            <SelectButton
              value={agrupacion}
              options={AGRUPACION_OPTIONS}
              onChange={(e) => e.value && setAgrupacion(e.value)}
              className="fd-select-sm"
              allowEmpty={false}
            />
          }
        >
          <ComboBarLineChart
            labels={periodoChart.labels}
            bars={periodoChart.bars}
            lines={periodoChart.lines}
          />
        </ChartCard>

        <ChartCard
          title="Distribución por estado"
          subtitle={
            estadosVista === "porSolicitud"
              ? "Estado de cabecera de la solicitud"
              : "Estado de cada línea"
          }
          loading={estados.loading}
          error={estados.error}
          onRetry={estados.reload}
          empty={!estadosChart.total}
          actions={
            <SelectButton
              value={estadosVista}
              options={ESTADOS_VISTA_OPTIONS}
              onChange={(e) => e.value && setEstadosVista(e.value)}
              className="fd-select-sm"
              allowEmpty={false}
            />
          }
        >
          <DoughnutChart
            labels={estadosChart.labels}
            values={estadosChart.values}
            colors={estadosChart.colors}
            height={320}
            centerLabel={formatNumber(estadosChart.total)}
            centerCaption={
              estadosVista === "porSolicitud" ? "solicitudes" : "líneas"
            }
          />
        </ChartCard>
      </div>

      {/* ------------------------- ESTADOS POR PERIODO + TIPOS ------------------------- */}
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title="Solicitudes por estado y período"
          subtitle="N° de solicitudes según su estado actual"
          loading={periodos.loading}
          error={periodos.error}
          onRetry={periodos.reload}
          empty={!periodos.data?.length}
        >
          <MultiBarChart
            labels={periodoChart.labels}
            series={periodoChart.estados}
            stacked
            money={false}
          />
        </ChartCard>

        <ChartCard
          title="Solicitudes por tipo"
          subtitle="Monto solicitado por tipo de documento"
          loading={tipos.loading}
          error={tipos.error}
          onRetry={tipos.reload}
          empty={!tipos.data?.length}
        >
          <div className="fd-split">
            <div className="fd-split-chart">
              <DoughnutChart
                labels={tiposChart.labels}
                values={tiposChart.values}
                money
                height={260}
                centerLabel={formatMoneyCompact(tiposChart.total)}
                centerCaption="solicitado"
              />
            </div>
            <div className="fd-split-table">
              <DataTable
                value={tipos.data ?? []}
                size="small"
                className="fd-table"
              >
                <Column header="Tipo" body={(t: TipoRow) => clean(t.nombre)} />
                <Column
                  header="Cant."
                  body={(t: TipoRow) => <Count value={t.cantidadSolicitudes} />}
                />
                <Column
                  header="Monto"
                  body={(t: TipoRow) => <Money value={t.montoClp} />}
                />
                <Column
                  header="Rech."
                  body={(t: TipoRow) => <Count value={t.rechazadas} />}
                />
              </DataTable>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* ------------------------- CENTROS DE COSTO ------------------------- */}
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title={`Top ${RANKING_CHART} centros de costo que más solicitan`}
          subtitle="Clic en una barra para filtrar el dashboard por ese centro de costo"
          loading={centros.loading}
          error={centros.error}
          onRetry={centros.reload}
          empty={!topCentros.length}
          height={420}
          actions={
            <SelectButton
              value={ordenCC}
              options={ORDENAR_POR_OPTIONS}
              onChange={(e) => e.value && setOrdenCC(e.value)}
              className="fd-select-sm"
              allowEmpty={false}
            />
          }
        >
          <MultiBarChart
            labels={centrosChart.labels}
            series={centrosChart.series}
            horizontal
            money={ordenCC !== "cantidad"}
            height={420}
            onBarClick={onCentroBarClick}
          />
        </ChartCard>

        <ChartCard
          title="Detalle por centro de costo"
          subtitle={`Hasta ${RANKING_LIMIT} centros de costo, ordenados por ${ordenCC}`}
          loading={centros.loading}
          error={centros.error}
          onRetry={centros.reload}
          height={420}
        >
          <DataTable
            value={centros.data ?? []}
            size="small"
            scrollable
            scrollHeight="420px"
            sortMode="single"
            removableSort
            stripedRows
            emptyMessage="Sin centros de costo para los filtros seleccionados"
            className="fd-table"
          >
            <Column
              header="Centro de costo"
              field="nombre"
              sortable
              style={{ minWidth: "12rem" }}
              body={(c: CentroCostoRow) => (
                <FilterLink
                  name={c.nombre}
                  sub={c.area}
                  title="Filtrar dashboard por este centro de costo"
                  onClick={() => filtros.filterByCentroCosto(c.idCentroCosto)}
                />
              )}
            />
            <Column
              header="Sol."
              field="cantidadSolicitudes"
              sortable
              body={(c: CentroCostoRow) => (
                <Count value={c.cantidadSolicitudes} />
              )}
            />
            <Column
              header="Solicitado"
              field="montoSolicitadoClp"
              sortable
              body={(c: CentroCostoRow) => (
                <Money value={c.montoSolicitadoClp} />
              )}
            />
            <Column
              header="% Aprob."
              field="tasaAprobacion"
              sortable
              style={{ minWidth: "8rem" }}
              body={(c: CentroCostoRow) => <PctBar value={c.tasaAprobacion} />}
            />
            <Column
              header="% a OC"
              field="tasaConversionAOrden"
              sortable
              style={{ minWidth: "8rem" }}
              body={(c: CentroCostoRow) => (
                <PctBar
                  value={c.tasaConversionAOrden}
                  color={CHART_COLORS.contabilizado}
                />
              )}
            />
          </DataTable>
        </ChartCard>
      </div>

      {/* ------------------------- USUARIOS + APROBADORES ------------------------- */}
      <div className="fd-grid fd-grid-1-1">
        <div ref={usuariosRef} className="fd-anchor">
          <ChartCard
            title="Usuarios que más solicitan"
            subtitle="Clic en un usuario para filtrar el dashboard"
            loading={usuarios.loading}
            error={usuarios.error}
            onRetry={usuarios.reload}
            height={420}
            actions={
              <SelectButton
                value={ordenUsuarios}
                options={ORDENAR_POR_OPTIONS}
                onChange={(e) => e.value && setOrdenUsuarios(e.value)}
                className="fd-select-sm"
                allowEmpty={false}
              />
            }
          >
            <DataTable
              value={usuarios.data ?? []}
              size="small"
              scrollable
              scrollHeight="420px"
              sortMode="single"
              removableSort
              stripedRows
              emptyMessage="Sin usuarios para los filtros seleccionados"
              className="fd-table"
            >
              <Column
                header="Usuario"
                field="usuario"
                sortable
                style={{ minWidth: "11rem" }}
                body={(u: UsuarioRow) => (
                  <FilterLink
                    name={u.usuario}
                    sub={`Última: ${formatFecha(u.ultimaSolicitud)}`}
                    title="Filtrar dashboard por este usuario"
                    onClick={() =>
                      filtros.filterByUsuario(u.idUsuario, u.usuario)
                    }
                  />
                )}
              />
              <Column
                header="Sol."
                field="cantidadSolicitudes"
                sortable
                body={(u: UsuarioRow) => (
                  <Count value={u.cantidadSolicitudes} />
                )}
              />
              <Column
                header="Solicitado"
                field="montoSolicitadoClp"
                sortable
                body={(u: UsuarioRow) => <Money value={u.montoSolicitadoClp} />}
              />
              <Column
                header="Pend."
                field="pendientes"
                sortable
                body={(u: UsuarioRow) => <Count value={u.pendientes} />}
              />
              <Column
                header="% Rechazo"
                field="tasaRechazo"
                sortable
                body={(u: UsuarioRow) => (
                  <span
                    className={`fd-num${u.tasaRechazo >= 10 ? " fd-neg" : ""}`}
                  >
                    {formatPercent(u.tasaRechazo)}
                  </span>
                )}
              />
              <Column
                header="Días a OC"
                field="promedioDiasAOrden"
                sortable
                headerTooltip="Promedio de días desde la solicitud hasta su OC"
                body={(u: UsuarioRow) => (
                  <span className="fd-num">
                    {formatDias(u.promedioDiasAOrden)}
                  </span>
                )}
              />
            </DataTable>
          </ChartCard>
        </div>

        <ChartCard
          title="Aprobadores de solicitudes"
          subtitle={`Top ${APROBADORES_LIMIT} por acciones de aprobación / confirmación`}
          loading={aprobadores.loading}
          error={aprobadores.error}
          onRetry={aprobadores.reload}
          height={420}
        >
          <DataTable
            value={aprobadores.data ?? []}
            size="small"
            scrollable
            scrollHeight="420px"
            sortMode="single"
            removableSort
            stripedRows
            emptyMessage="Sin aprobaciones para los filtros seleccionados"
            className="fd-table"
          >
            <Column
              header="Aprobador"
              field="usuario"
              sortable
              style={{ minWidth: "10rem" }}
              body={(a: AprobadorRow) => (
                <span className="fd-prov-name">{clean(a.usuario)}</span>
              )}
            />
            <Column
              header="Sol."
              field="solicitudes"
              sortable
              body={(a: AprobadorRow) => <Count value={a.solicitudes} />}
            />
            <Column
              header="Aprob."
              field="aprobaciones"
              sortable
              body={(a: AprobadorRow) => <Count value={a.aprobaciones} />}
            />
            <Column
              header="Conf."
              field="confirmaciones"
              sortable
              body={(a: AprobadorRow) => <Count value={a.confirmaciones} />}
            />
            <Column
              header="Rech."
              field="rechazos"
              sortable
              body={(a: AprobadorRow) => <Count value={a.rechazos} />}
            />
            <Column
              header="Días prom."
              field="promedioDiasDesdeCreacion"
              sortable
              headerTooltip="Promedio de días desde la creación de la solicitud hasta la acción"
              body={(a: AprobadorRow) => (
                <span className="fd-num">
                  {formatDias(a.promedioDiasDesdeCreacion)}
                </span>
              )}
            />
          </DataTable>
        </ChartCard>
      </div>

      {/* ------------------------- SUBROGANCIA ------------------------- */}
      <SubroganciaSection
        tipo="solicitudes"
        global={subrogancia}
        propia={subroganciaPropia}
        verTodo={verTodaSubrogancia}
        query={query}
        queryKey={queryKey}
      />

      {/* ------------------------- TRAZABILIDAD ------------------------- */}
      <div ref={trazabilidadRef} className="fd-anchor">
        <TrazabilidadCard
          filtros={filtros}
          buscarPor="idSolicitud"
          refreshKey={refreshKey}
        />
      </div>
    </div>
  );
}
